import { useLayoutEffect, useRef, useEffect, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

import { QUERY_NAME_DASHBOARD } from '../constants/query-params';
import { useActions } from './useActions';
import { useTypedSelector } from './useTypedSelector';
import useReportLoader, { MetricOrGroupingItem, ReportData } from './useReportLoader';
import { unApi } from '../api/endpoints/reports/unApi';
import { isAbortError } from '../../../helpers/utils/reportApiUtils';
import { devLog, devError, devWarn } from '../../../helpers/utils/reportValidation';

/**
 * URL параметры для загрузки отчёта.
 */
export const QUERY_REPORT_HASH = 'report_hash';
export const QUERY_CONFIG = 'config';

export interface UseReportPageInitOptions {
    /** Дефолтные метрики для этого типа отчёта */
    defaultMetrics: MetricOrGroupingItem[];
    /** Дефолтные группировки для этого типа отчёта */
    defaultGroupings: MetricOrGroupingItem[];
    /** Callback после загрузки дефолтных настроек (опционально) */
    onDefaultsLoaded?: () => void;
}

/**
 * Универсальный хук для инициализации страницы отчёта.
 * Обрабатывает загрузку сохранённого отчёта из URL или дефолтных настроек.
 * 
 * Использует useReportLoader для унифицированной загрузки отчётов.
 * 
 * Использование:
 * ```tsx
 * const { isTableLoading } = useReportPageInit({
 *     defaultMetrics: defaultMetricsOrder,
 *     defaultGroupings: defaultGroupingsOrder,
 * });
 * ```
 */
export const useReportPageInit = (options: UseReportPageInitOptions) => {
    const { defaultMetrics, defaultGroupings, onDefaultsLoaded } = options;
    
    const [searchParams] = useSearchParams();
    const location = useLocation();
    
    // Поддержка нескольких способов передачи отчёта через URL:
    // 1. ?dashboard=<hash> — legacy формат (QUERY_NAME_DASHBOARD)
    // 2. ?report_hash=<hash> — новый формат для прямой ссылки на отчёт
    // 3. ?config=<base64> — inline конфигурация (для перехода из дашборда)
    const legacyReportHash = searchParams.get(QUERY_NAME_DASHBOARD);
    const newReportHash = searchParams.get(QUERY_REPORT_HASH);
    const configBase64 = searchParams.get(QUERY_CONFIG);
    
    // Определяем источник конфигурации
    const reportHash = newReportHash || legacyReportHash;
    const hasSavedReport = !!reportHash;
    const hasInlineConfig = !!configBase64;
    
    // Декодируем inline конфигурацию если есть
    const inlineConfig = useMemo((): ReportData | null => {
        if (!configBase64) return null;
        
        try {
            const configJson = decodeURIComponent(atob(configBase64));
            const config = JSON.parse(configJson) as ReportData;
            devLog('[useReportPageInit] Декодирована inline конфигурация:', config);
            return config;
        } catch (error) {
            devError('[useReportPageInit] Ошибка декодирования inline конфигурации:', error);
            return null;
        }
    }, [configBase64]);
    
    // Refs для предотвращения лишних запросов
    const currentReportHashRef = useRef<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    
    // Redux state
    const { isTableLoading, isChartLoading } = useTypedSelector((state) => state.meta);
    const { currentReport } = useTypedSelector((state) => state.reports);
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    
    // Обновляем ref при изменении currentReport
    currentReportHashRef.current = currentReport?.hash || null;
    
    // Ref для отслеживания, был ли уже загружен отчёт с savedSearch фильтрами
    const savedSearchLoadedRef = useRef<string | null>(null);
    
    // Actions для cleanup
    const {
        setFilters,
        setFiltersTable,
        setCurrentReport,
        setCurrentSegment,
        setSortTable,
        setMetaCompare,
        setCurrentFunnel,
    } = useActions();
    
    // Используем useReportLoader с кастомными дефолтами
    const { loadReport, loadDefaults } = useReportLoader({
        customDefaultMetrics: defaultMetrics,
        customDefaultGroupings: defaultGroupings,
        onLoadComplete: onDefaultsLoaded,
    });
    
    // Refs для функций загрузки
    const loadReportRef = useRef(loadReport);
    const loadDefaultsRef = useRef(loadDefaults);
    loadReportRef.current = loadReport;
    loadDefaultsRef.current = loadDefaults;
    
    // Основной эффект инициализации
    useLayoutEffect(() => {
        // Приоритет загрузки:
        // 1. Inline конфигурация (?config=<base64>)
        // 2. Сохранённый отчёт по hash (?report_hash=<hash> или ?dashboard=<hash>)
        // 3. Дефолтные настройки
        
        if (hasInlineConfig && inlineConfig) {
            // Загружаем inline конфигурацию
            devLog('[useReportPageInit] Загружаем inline конфигурацию');
            loadReportRef.current(inlineConfig);
        } else if (hasSavedReport && reportHash) {
            // Проверяем, не загружен ли уже этот отчёт
            if (currentReportHashRef.current === reportHash) {
                return;
            }
            
            // Отменяем предыдущий запрос
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            const controller = new AbortController();
            abortControllerRef.current = controller;
            
            devLog('[useReportPageInit] Загружаем сохранённый отчёт:', reportHash);
            
            unApi.getReportDataFromHash(reportHash, { signal: controller.signal })
                .then(async (response) => {
                    if (controller.signal.aborted) return;
                    await loadReportRef.current(response.data[0]);
                })
                .catch((error) => {
                    if (isAbortError(error)) {
                        return;
                    }
                    devError('[useReportPageInit] Ошибка загрузки отчёта:', error);
                    // При ошибке загружаем дефолтные настройки
                    loadDefaultsRef.current();
                });
        } else {
            // Для нового отчёта — загружаем дефолтные настройки через useReportLoader
            loadDefaultsRef.current();
        }

        // Cleanup
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            setMetaCompare({ value: false });
            setFilters([]);
            setFiltersTable([]);
            setCurrentReport({});
            setSortTable({});
            setCurrentFunnel([]);
            setCurrentSegment(null);
        };
    }, [hasSavedReport, reportHash, hasInlineConfig, inlineConfig, location.key]);
    
    // Дополнительный эффект: перезагрузка отчёта когда школа загрузится
    // Это нужно для savedSearch фильтров, которые требуют schoolId для загрузки значений
    useEffect(() => {
        // Если нет сохранённого отчёта или нет школы — выходим
        if (!hasSavedReport || !reportHash || !selectedSchool?.id) {
            return;
        }
        
        // Если отчёт уже загружен с этим hash и школой — выходим
        const loadKey = `${reportHash}_${selectedSchool.id}`;
        if (savedSearchLoadedRef.current === loadKey) {
            return;
        }
        
        // Проверяем, есть ли в текущем отчёте savedSearch фильтры без загруженных значений
        const filters = currentReport?.values?.filters;
        if (!Array.isArray(filters)) {
            return;
        }
        
        const hasSavedSearchWithoutValues = filters.some(
            (f: any) => f.savedSearch && f.type === 'lazylist' && (!f.checkeds || f.checkeds.length === 0)
        );
        
        if (!hasSavedSearchWithoutValues) {
            // Отмечаем как загруженный, чтобы не перезагружать повторно
            savedSearchLoadedRef.current = loadKey;
            return;
        }
        
        devLog('[useReportPageInit] Школа загружена, перезагружаем отчёт с savedSearch фильтрами');
        savedSearchLoadedRef.current = loadKey;
        
        // Перезагружаем отчёт
        unApi.getReportDataFromHash(reportHash)
            .then(async (response) => {
                await loadReportRef.current(response.data[0]);
            })
            .catch((error) => {
                devWarn('[useReportPageInit] Ошибка перезагрузки отчёта:', error);
            });
    }, [selectedSchool?.id, hasSavedReport, reportHash, currentReport]);
    
    return {
        isTableLoading,
        isChartLoading,
        hasSavedReport,
        reportHash,
        hasInlineConfig,
        inlineConfig,
    };
};

export default useReportPageInit;
